import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  createAccountConfig,
  deleteAccountConfig,
  getAccountConfigs,
  updateAccountConfig,
} from '@shared/api/accountConfig/accountConfig.controller';
import {
  AccountConfig,
  AccountConfigCreate,
  AccountEntity,
} from '@shared/api/accountConfig/accountConfig.interface';
import { updateBugoBrand } from '@shared/api/bugoBrand/bugoBrand.controller';
import { BugoBrand } from '@shared/api/bugoBrand/bugoBrand.interface';
import { sellerShopStateSelectorFamily } from '@shared/api/shop/shop.selector';
import { Loading } from '@shared/components/Loading';
import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';
import {
  Card,
  Checkbox,
  Divider,
  Input,
  message,
  Modal,
  Select,
  Switch,
  Table,
  TableColumnsType,
  Tag,
} from 'antd';
import { useParams } from 'react-router-dom';

import { useSellerTopNavHook } from '../hooks/useSellerTopNavHook';

// ─── AccountConfig 라벨 유틸 ───
const accountEntityLabel = (entity: AccountEntity) => {
  switch (entity) {
    case AccountEntity.Mari:
      return '마리';
    case AccountEntity.Gaon:
      return '가온';
    case AccountEntity.NotDecided:
      return '미정';
    default:
      return entity;
  }
};

const accountEntityColor = (entity: AccountEntity) => {
  switch (entity) {
    case AccountEntity.Mari:
      return 'blue';
    case AccountEntity.Gaon:
      return 'green';
    case AccountEntity.NotDecided:
      return 'default';
    default:
      return 'default';
  }
};

// ─── BugoBrand 타입 (전체 목록용, accountConfig 포함) ───
interface BugoBrandListItem {
  _id: string;
  type: string;
  nickName: string; // bugoAgency.nickName
  accountConfig?: AccountConfig | string | null;
}

// ─── 메인 페이지 ───
const SellerAccountSettingsPage: FC = function SellerAccountSettingsPage() {
  useSellerTopNavHook('관리자설정');
  const { shopId } = useParams();
  const shopId_ = shopId ?? 'undefined';
  const shopState = useRecoilValue(sellerShopStateSelectorFamily(shopId_));

  const shop = useMemo(() => {
    if (shopState.status === 'success' && shopState.data) {
      return shopState.data;
    }
    return null;
  }, [shopState.data, shopState.status]);

  // ─── State ───
  const [accountConfigs, setAccountConfigs] = useState<AccountConfig[]>([]);
  const [allBugoBrands, setAllBugoBrands] = useState<BugoBrandListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Shop에 연결된 bugoBrand ID set
  const connectedBrandIds = useMemo(() => {
    if (!shop) return new Set<string>();
    return new Set(shop.bugoBrands.map((b) => b._id));
  }, [shop]);

  // ─── AccountConfig CRUD Modal ───
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AccountConfig | null>(null);
  const [configForm, setConfigForm] = useState<AccountConfigCreate>({
    name: '',
    accountEntity: AccountEntity.NotDecided,
    pgFeeRefund: false,
  });

  // ─── 데이터 로드 ───
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configs, brandsRes] = await Promise.all([
        getAccountConfigs(),
        api_.get<BugoBrand[]>('admin/bugo-brand'),
      ]);
      setAccountConfigs(configs);
      setAllBugoBrands(
        brandsRes.data.map((b) => ({
          _id: b._id,
          type: b.type,
          nickName: b.bugoAgency?.nickName ?? b.bugoFuneralHome?.funeralHomeInfo ?? b._id,
          accountConfig: b.accountConfig ?? null,
        })),
      );
    } catch (err) {
      console.error(err);
      message.error('데이터 로드에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── AccountConfig 저장 ───
  const handleSaveConfig = useCallback(async () => {
    if (!configForm.name.trim()) {
      message.warning('설정 이름을 입력해주세요.');
      return;
    }
    setSaving(true);
    try {
      if (editingConfig) {
        await updateAccountConfig({
          _id: editingConfig._id,
          ...configForm,
        });
        message.success('정산설정이 수정되었습니다.');
      } else {
        await createAccountConfig(configForm);
        message.success('정산설정이 생성되었습니다.');
      }
      setConfigModalOpen(false);
      setEditingConfig(null);
      await fetchData();
    } catch (err) {
      console.error(err);
      message.error('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }, [configForm, editingConfig, fetchData]);

  const handleDeleteConfig = useCallback(
    async (id: string) => {
      Modal.confirm({
        title: '정산설정 삭제',
        content: '정말 삭제하시겠습니까? 연결된 상조브랜드의 정산설정이 해제됩니다.',
        okText: '삭제',
        cancelText: '취소',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await deleteAccountConfig(id);
            message.success('삭제되었습니다.');
            await fetchData();
          } catch (err) {
            console.error(err);
            message.error('삭제에 실패했습니다.');
          }
        },
      });
    },
    [fetchData],
  );

  const openCreateConfigModal = useCallback(() => {
    setEditingConfig(null);
    setConfigForm({
      name: '',
      accountEntity: AccountEntity.NotDecided,
      pgFeeRefund: false,
    });
    setConfigModalOpen(true);
  }, []);

  const openEditConfigModal = useCallback((config: AccountConfig) => {
    setEditingConfig(config);
    setConfigForm({
      name: config.name,
      accountEntity: config.accountEntity,
      pgFeeRefund: config.pgFeeRefund,
    });
    setConfigModalOpen(true);
  }, []);

  // ─── Shop bugoBrands 연결/해제 ───
  const handleToggleBrandConnection = useCallback(
    async (brandId: string, connect: boolean) => {
      if (!shop) return;
      setSaving(true);
      try {
        const currentIds = shop.bugoBrands.map((b) => b._id);
        const newIds = connect
          ? [...currentIds, brandId]
          : currentIds.filter((id) => id !== brandId);
        await api_.patch(`admin/shop/${shop._id}`, { bugoBrands: newIds });
        message.success(
          connect ? '상조브랜드가 연결되었습니다.' : '상조브랜드 연결이 해제되었습니다.',
        );
        // shopState를 직접 재로드하기 위해 페이지 새로고침
        window.location.reload();
      } catch (err) {
        console.error(err);
        message.error('연결 변경에 실패했습니다.');
      } finally {
        setSaving(false);
      }
    },
    [shop],
  );

  // ─── BugoBrand accountConfig 변경 ───
  const handleChangeBrandAccountConfig = useCallback(
    async (brandId: string, accountConfigId: string | null) => {
      setSaving(true);
      try {
        await updateBugoBrand(
          { _id: brandId, accountConfig: accountConfigId },
          LowerRole.Admin,
        );
        message.success('정산설정이 변경되었습니다.');
        await fetchData();
      } catch (err) {
        console.error(err);
        message.error('정산설정 변경에 실패했습니다.');
      } finally {
        setSaving(false);
      }
    },
    [fetchData],
  );

  // ─── AccountConfig 테이블 컬럼 ───
  const configColumns: TableColumnsType<AccountConfig> = useMemo(
    () => [
      {
        title: '이름',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: '정산주체',
        dataIndex: 'accountEntity',
        key: 'accountEntity',
        render: (entity: AccountEntity) => (
          <Tag color={accountEntityColor(entity)}>{accountEntityLabel(entity)}</Tag>
        ),
      },
      {
        title: 'PG수수료 반환',
        dataIndex: 'pgFeeRefund',
        key: 'pgFeeRefund',
        align: 'center',
        render: (val: boolean) =>
          val ? <Tag color="blue">반환</Tag> : <Tag>미반환</Tag>,
      },
      {
        title: '연결된 상조',
        key: 'linkedBrands',
        render: (_, record) => {
          const linked = allBugoBrands.filter((b) => {
            const configId =
              typeof b.accountConfig === 'object' && b.accountConfig
                ? b.accountConfig._id
                : b.accountConfig;
            return configId === record._id;
          });
          if (linked.length === 0) return <span className="text-gray-400">없음</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {linked.map((b) => (
                <Tag key={b._id}>{b.nickName}</Tag>
              ))}
            </div>
          );
        },
      },
      {
        title: '관리',
        key: 'action',
        align: 'center',
        render: (_, record) => (
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-100"
              onClick={() => openEditConfigModal(record)}
            >
              수정
            </button>
            <button
              type="button"
              className="rounded border border-red-300 bg-white px-3 py-1 text-xs text-red-600 hover:bg-red-50"
              onClick={() => handleDeleteConfig(record._id)}
            >
              삭제
            </button>
          </div>
        ),
      },
    ],
    [allBugoBrands, handleDeleteConfig, openEditConfigModal],
  );

  // ─── BugoBrand 테이블 컬럼 ───
  const brandColumns: TableColumnsType<BugoBrandListItem> = useMemo(
    () => [
      {
        title: '상조브랜드',
        dataIndex: 'nickName',
        key: 'nickName',
      },
      {
        title: '타입',
        dataIndex: 'type',
        key: 'type',
        render: (type: string) => (
          <Tag color={type === 'BugoAgency' ? 'cyan' : 'orange'}>
            {type === 'BugoAgency' ? '상조' : '장례식장'}
          </Tag>
        ),
      },
      {
        title: 'Shop 연결',
        key: 'connected',
        align: 'center',
        render: (_, record) => (
          <Checkbox
            checked={connectedBrandIds.has(record._id)}
            disabled={saving}
            onChange={(e) => handleToggleBrandConnection(record._id, e.target.checked)}
          />
        ),
      },
      {
        title: '정산설정',
        key: 'accountConfig',
        render: (_, record) => {
          const currentConfigId =
            typeof record.accountConfig === 'object' && record.accountConfig
              ? record.accountConfig._id
              : (record.accountConfig as string | null) ?? undefined;
          return (
            <Select
              className="w-full"
              style={{ minWidth: 150 }}
              placeholder="정산설정 선택"
              allowClear
              value={currentConfigId}
              disabled={saving}
              onChange={(val) => handleChangeBrandAccountConfig(record._id, val ?? null)}
              options={accountConfigs.map((c) => ({
                value: c._id,
                label: `${c.name} (${accountEntityLabel(c.accountEntity)})`,
              }))}
            />
          );
        },
      },
    ],
    [
      accountConfigs,
      connectedBrandIds,
      saving,
      handleToggleBrandConnection,
      handleChangeBrandAccountConfig,
    ],
  );

  if (!shop) {
    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 pb-8 sm:px-6 lg:px-8">
      <div className="space-y-8 py-6">
        {/* ─── 정산설정(AccountConfig) 관리 ─── */}
        <Card title="정산설정 관리">
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              onClick={openCreateConfigModal}
            >
              새 정산설정 추가
            </button>
          </div>
          <Table
            columns={configColumns}
            dataSource={accountConfigs}
            rowKey="_id"
            pagination={false}
            size="small"
            bordered
          />
        </Card>

        <Divider />

        {/* ─── 상조브랜드 연결 & 정산설정 배정 ─── */}
        <Card title={`상조브랜드 관리 — ${shop.name}`}>
          <p className="mb-4 text-sm text-gray-500">
            Shop에 연결할 상조브랜드를 선택하고, 각 브랜드에 정산설정을 배정하세요.
          </p>
          <Table
            columns={brandColumns}
            dataSource={allBugoBrands}
            rowKey="_id"
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            size="small"
            bordered
          />
        </Card>
      </div>

      {/* ─── AccountConfig 생성/수정 모달 ─── */}
      <Modal
        title={editingConfig ? '정산설정 수정' : '새 정산설정 추가'}
        open={configModalOpen}
        onOk={handleSaveConfig}
        onCancel={() => {
          setConfigModalOpen(false);
          setEditingConfig(null);
        }}
        confirmLoading={saving}
        okText={editingConfig ? '수정' : '생성'}
        cancelText="취소"
      >
        <div className="space-y-4 py-2">
          <div>
            <label className="mb-1 block text-sm font-medium">설정 이름</label>
            <Input
              placeholder="예: 마리 기본설정"
              value={configForm.name}
              onChange={(e) =>
                setConfigForm((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">정산주체</label>
            <Select
              className="w-full"
              value={configForm.accountEntity}
              onChange={(val) =>
                setConfigForm((prev) => ({ ...prev, accountEntity: val }))
              }
              options={[
                { value: AccountEntity.Mari, label: '마리' },
                { value: AccountEntity.Gaon, label: '가온' },
                { value: AccountEntity.NotDecided, label: '미정' },
              ]}
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium">PG수수료 반환</label>
            <Switch
              checked={configForm.pgFeeRefund}
              onChange={(checked) =>
                setConfigForm((prev) => ({ ...prev, pgFeeRefund: checked }))
              }
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SellerAccountSettingsPage;
